import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyContent,
  ErrorHandler,
  FilteredTable,
  Link,
  MultiSelectTableFilter,
  StatusPill,
  Text,
} from '@common/components';
import { FontWeight } from '@common/tokens';
import { useNavigate, useQuery, usePaginationHelper } from '@common/hooks';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import { datetime } from '@common/services';
import type {
  LoadBalancingScheduleDashboardOrderField,
  LoadBalancingScheduleDashboardStatus,
  OrderDirection,
  ScheduleDashboardPeriodsQueryVariables,
} from '@common/graphql';
import {
  formatFillRate,
  periodLinkTo,
  type SchedulingPeriodStatusType,
  statusLabel,
  statusPillVariant,
} from '@load-balancing/services';
import { SCHEDULE_DASHBOARD_PERIODS_QUERY } from '@load-balancing/graphql/queries/schedule-dashboard-periods';
import { SERVICE_LINES_QUERY } from '@load-balancing/graphql/queries/service-lines';
import { TeamReviewPeriodMenu } from './_team-review-period-menu';

const DEFAULT_PAGE_SIZE = 10;
const LOCALSTORAGE_FILTER_KEY = 'scheduleDashboardPeriods';

// Table column key → server `orderField` enum value (drives whitelisted sorting of the
// unit×period child rows within each group).
const COLUMNS_DICTIONARY: Record<
  string,
  LoadBalancingScheduleDashboardOrderField
> = {
  period: 'PERIOD',
  unit: 'UNIT',
  fillRate: 'FILL_RATE',
  lastEditedAt: 'LAST_EDITED',
  dueDate: 'DUE_DATE',
  status: 'STATUS',
};

// Multi-select Status column filter → server `statuses` argument (OR across the picks).
// The four options mirror the compact pills (see scheduling-period-status). Each option
// expands to the backend enum value(s) it covers via STATUS_FILTER_TO_BACKEND; the
// 'Balancing' bucket spans every load-balancing-phase state, which all render as the one
// Balancing pill.
const STATUS_FILTER_OPTIONS: { text: string; value: string }[] = [
  { text: 'Workload Planning', value: 'WORKLOAD_PLANNING' },
  { text: 'Self-Scheduling', value: 'SELF_SCHEDULING' },
  { text: 'Balancing', value: 'BALANCING' },
  { text: 'Completed', value: 'COMPLETED' },
];

// Compact filter option value → the backend statuses it selects. 'Balancing' fans out to
// run_pending / balancing / overdue so the filter matches the collapsed pill.
const STATUS_FILTER_TO_BACKEND: Record<
  string,
  LoadBalancingScheduleDashboardStatus[]
> = {
  WORKLOAD_PLANNING: ['WORKLOAD_PLANNING'],
  SELF_SCHEDULING: ['SELF_SCHEDULING'],
  BALANCING: ['RUN_PENDING', 'BALANCING', 'OVERDUE'],
  COMPLETED: ['COMPLETED'],
};

// Reconcile the backend's UPPER_CASE status enum to the FE's lower-case union. An
// exhaustive Record (not `.toLowerCase() as`) makes a new backend enum value from
// `yarn codegen` a compile error here rather than a silently-dropped runtime value.
const STATUS_FROM_BACKEND: Record<
  LoadBalancingScheduleDashboardStatus,
  SchedulingPeriodStatusType
> = {
  WORKLOAD_PLANNING: 'workload_planning',
  SELF_SCHEDULING: 'self_scheduling',
  RUN_PENDING: 'run_pending',
  BALANCING: 'balancing',
  OVERDUE: 'overdue',
  COMPLETED: 'completed',
};

// A service-line group header row: a collapsible parent whose `children` are its unit rows.
type GroupRowType = {
  id: string; // serviceLineId — stable expand key
  isGroup: true;
  serviceLineId: string;
  serviceLineName: string;
  needsTeamReview: boolean;
  // Backend-resolved deep-link target for the "Review Team" CTA: the period awaiting review.
  // Derived server-side from the full row set, so it's present even when filters hide that
  // period (it tracks `needsTeamReview`); undefined only when nothing awaits review.
  teamReviewPeriodId?: string;
  children: UnitRowType[];
};

// A unit×period child row, with status narrowed to the FE union and the nullable run id
// collapsed to `undefined`.
type UnitRowType = {
  id: string; // `${serviceLineId}:${periodRowId}` — unique across groups
  isGroup: false;
  serviceLineId: string;
  periodStart: string;
  periodEnd: string;
  unit: string;
  costCenter: string;
  fillRate: number | null;
  lastEditedAt: string;
  dueDate: string;
  status: SchedulingPeriodStatusType;
  runId?: string;
  organizationUnitId: string;
  teamReviewComplete: boolean;
};

type DashboardRowType = GroupRowType | UnitRowType;

// Schedule Dashboard table: a service-line-grouped, expandable view across every service
// line the manager can see. Group header rows collapse their unit×period children (all
// expanded by default); rows come from the server-driven `scheduleDashboardPeriods` query
// (filter/sort, group-level pagination via `usePaginationHelper`). The Service Line column
// filter replaces the old left-nav switcher; Fill Rate / Last Edited stay hidden until a
// period's team review is complete.
export function SchedulePeriodsTable() {
  const navigate = useNavigate();
  // TODO(WORK-6503): debug-only flag — remove this and every isTeamReviewPeriodPickerEnabled
  // branch below when the debug Review Team behavior is torn down.
  // https://app.asana.com/1/303134204339906/project/1213809590907449/task/1216012235109932
  const { isTeamReviewPeriodPickerEnabled } = useFeatureFlags();
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCALSTORAGE_FILTER_KEY,
    columnsDictionary: COLUMNS_DICTIONARY,
    // The dashboard always loads unfiltered — no seeded default, and stored filters are not
    // re-applied on load (an empty override beats the localStorage read inside the hook).
    filtersOverrideDefault: {},
    // Don't persist sort/pagination either, so every page load starts at the default below.
    savePaginationInLocalStorage: false,
    // No FE-seeded sort: the backend owns the default order (Due Date, DESC). Leaving
    // orderField/orderDirection unset keeps every column un-sorted on load, so no column shows
    // the grey "currently sorting" background until the user deliberately picks a sort.
    defaultPaginationData: {
      defaultPageSize: DEFAULT_PAGE_SIZE,
      limit: DEFAULT_PAGE_SIZE,
      offset: 0,
    },
  });

  const variables = useMemo<ScheduleDashboardPeriodsQueryVariables>(() => {
    const { offset, limit, orderField, orderDirection } = paginationVariables;
    // Expand each picked compact bucket into the backend statuses it covers (e.g.
    // 'Balancing' → run_pending/balancing/overdue), then OR them server-side.
    const statusFilters = (filters.status as string[] | null) ?? null;
    const statuses = statusFilters?.length
      ? statusFilters.flatMap((value) => STATUS_FILTER_TO_BACKEND[value] ?? [])
      : null;
    return {
      statuses,
      limit: limit ?? DEFAULT_PAGE_SIZE,
      offset: offset ?? 0,
      // Only send a sort once the user picks a column; otherwise omit both args so the backend
      // applies its own default order (Due Date, DESC) and no column reads as sorted.
      ...(orderField
        ? {
            orderField: orderField as LoadBalancingScheduleDashboardOrderField,
            orderDirection: (orderDirection as OrderDirection) ?? 'DESC',
          }
        : {}),
      filters: {
        // Service Line, Period and Unit are multi-select lists of uuids (Service Line ids are
        // matched within the caller's authorized set; out-of-scope ids match no rows).
        serviceLineIds: filters.serviceLine ?? null,
        unitIds: filters.unit ?? null,
        periodIds: filters.period ?? null,
        // Due Date is a [from, to] date-range tuple.
        dueDateFrom: filters.dueDate?.[0] ?? null,
        dueDateTo: filters.dueDate?.[1] ?? null,
      },
    };
  }, [paginationVariables, filters]);

  // cache-and-network so a run finished elsewhere (e.g. just generated from Team Review, then
  // navigated back here) is reflected on mount without a manual refresh: the cached rows render
  // instantly while a network fetch refreshes them. Default cache-first would serve stale rows
  // since this table unmounts on navigation and re-reads the populated cache on return.
  const { data, loading, error } = useQuery(SCHEDULE_DASHBOARD_PERIODS_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
  });
  const { data: serviceLinesData } = useQuery(SERVICE_LINES_QUERY);

  const result = data?.scheduleDashboardPeriods;

  const rows = useMemo<GroupRowType[]>(
    () =>
      (result?.groups ?? []).map((group) => ({
        id: group.serviceLineId,
        isGroup: true,
        serviceLineId: group.serviceLineId,
        serviceLineName: group.serviceLineName,
        needsTeamReview: group.needsTeamReview,
        teamReviewPeriodId: group.teamReviewSchedulingPeriodId ?? undefined,
        children: group.periods.map((period) => ({
          id: `${group.serviceLineId}:${period.id}`,
          isGroup: false,
          serviceLineId: group.serviceLineId,
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          unit: period.unit,
          costCenter: period.costCenter,
          fillRate: period.fillRate ?? null,
          lastEditedAt: period.lastEditedAt,
          dueDate: period.dueDate,
          status: STATUS_FROM_BACKEND[period.status],
          runId: period.runId ?? undefined,
          organizationUnitId: period.organizationUnitId,
          teamReviewComplete: period.teamReviewComplete,
        })),
      })),
    [result]
  );

  // All groups expanded by default; re-expand whenever the group set changes (a new page,
  // sort, or filter). Toggling a chevron collapses just that group until the next change.
  const groupIds = useMemo(() => rows.map((group) => group.id), [rows]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  useEffect(() => {
    setExpandedRowKeys(groupIds);
  }, [groupIds]);

  function toggleExpand(key: string) {
    setExpandedRowKeys((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    );
  }

  // Service Line filter options span every service line (not just the current page).
  const serviceLineFilters = useMemo(
    () =>
      (serviceLinesData?.serviceLines ?? []).map((line) => ({
        text: line.name,
        value: line.id,
      })),
    [serviceLinesData]
  );

  // Period and Unit filter options come from the server's `filterOptions` (the full authorized,
  // windowed set — independent of the current page or active filters), so the dropdowns always
  // offer every choice. Period options are the scheduling periods from the last 3 months onward
  // (value = period uuid, label = the same date range the Period column renders); Unit options are
  // the authorized service-line units (value = unit uuid, label = unit name).
  const periodFilters = useMemo(
    () =>
      (result?.filterOptions.periods ?? []).map((period) => ({
        text: `${datetime(period.periodStart).format('MMM D')} – ${datetime(
          period.periodEnd
        ).format('MMM D, YYYY')}`,
        value: period.id,
      })),
    [result]
  );
  const unitFilters = useMemo(
    () =>
      (result?.filterOptions.units ?? []).map((unit) => ({
        text: unit.name,
        value: unit.id,
      })),
    [result]
  );

  // Periods offered by the flag-gated "Review Team" picker: the dashboard's full period
  // set (org-level, so the same list applies to every group) narrowed to the current +
  // future ones — periods whose end is today or later. Only computed/used when the
  // `team_review_period_picker` flag is on.
  // TODO(WORK-6503): remove with the debug Review Team behavior.
  const futurePeriods = useMemo(
    () =>
      (result?.filterOptions.periods ?? []).filter(
        (period) => !datetime(period.periodEnd).isBefore(datetime(), 'day')
      ),
    [result]
  );

  if (error) {
    return <ErrorHandler error={error} log="SchedulePeriodsTable" />;
  }

  // Reflect the active server sort on the column headers so the default (Due Date, DESC) —
  // and whatever column the user sorts afterward — shows its direction arrow. Sorting is
  // server-driven, so only the column matching `orderField` carries a direction; the rest
  // read as unsorted. Keeps the visible indicator in lockstep with `paginationVariables`.
  const { orderField, orderDirection } = paginationVariables;
  function sortOrderFor(columnKey: string): 'ascend' | 'descend' | null {
    if (COLUMNS_DICTIONARY[columnKey] !== orderField) return null;
    return orderDirection === 'DESC' ? 'descend' : 'ascend';
  }

  const columns = [
    {
      title: 'Service Line',
      key: 'serviceLine',
      sorter: false,
      filters: serviceLineFilters,
      // Multi-select, matching the Unit/Period column filters (no single-select radios).
      filterMultiple: true,
      filterDropdown: MultiSelectTableFilter,
      // Controlled (not defaultFilteredValue): keeps AntD's column state in sync with the
      // hook's `filters` so removing a pill sticks and isn't reapplied on the next sort.
      filteredValue: filters.serviceLine ?? null,
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? (
          <Text className="schedule-periods-table__group-name">
            {row.serviceLineName}
          </Text>
        ) : null,
    },
    {
      title: 'Period',
      key: 'period',
      sorter: true,
      sortOrder: sortOrderFor('period'),
      filters: periodFilters,
      filterMultiple: true,
      filterDropdown: MultiSelectTableFilter,
      filteredValue: filters.period ?? null,
      render: (_: unknown, row: DashboardRowType) => {
        if (row.isGroup) return null;

        // The Period cell links to the unit-scoped Schedule Review session for rows with a
        // reviewable run (balancing/overdue); plain text otherwise.
        const to = periodLinkTo(row.status, {
          serviceLineId: row.serviceLineId,
          runId: row.runId,
          organizationUnitId: row.organizationUnitId,
        });

        return to ? (
          <Link to={to}>{formatPeriod(row)}</Link>
        ) : (
          <Text>{formatPeriod(row)}</Text>
        );
      },
    },
    {
      title: 'Unit / Cost Center',
      key: 'unit',
      sorter: true,
      sortOrder: sortOrderFor('unit'),
      filters: unitFilters,
      filterMultiple: true,
      filterDropdown: MultiSelectTableFilter,
      filteredValue: filters.unit ?? null,
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? null : (
          <div>
            <Text block>{row.unit}</Text>
            <Text size="sm" secondary>
              {row.costCenter}
            </Text>
          </div>
        ),
    },
    {
      title: 'Fill Rate',
      key: 'fillRate',
      sorter: true,
      sortOrder: sortOrderFor('fillRate'),
      // Show the calculated fill rate whenever it exists (formatFillRate renders "—" for null),
      // matching main — not gated on team review completion.
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? null : <Text>{formatFillRate(row.fillRate)}</Text>,
    },
    {
      title: 'Last Edited',
      key: 'lastEditedAt',
      sorter: true,
      sortOrder: sortOrderFor('lastEditedAt'),
      // Show the last-edited timestamp for every row, matching main — not gated on team review.
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? null : (
          <Text>{datetime(row.lastEditedAt).format('MMM D, YYYY h:mm A')}</Text>
        ),
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      sorter: true,
      sortOrder: sortOrderFor('dueDate'),
      filterFormat: 'dateRange',
      filteredValue: filters.dueDate ?? null,
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? null : (
          <Text color={isPastDue(row) ? 'coral' : undefined}>
            {datetime(row.dueDate).format('MMM D, YYYY')}
          </Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      filters: STATUS_FILTER_OPTIONS,
      filterMultiple: true,
      filteredValue: filters.status ?? null,
      render: (_: unknown, row: DashboardRowType) =>
        row.isGroup ? null : (
          <StatusPill variant={statusPillVariant(row.status)}>
            {statusLabel(row.status)}
          </StatusPill>
        ),
    },
    {
      title: '',
      key: 'actions',
      sorter: false,
      render: (_: unknown, row: DashboardRowType) => {
        if (!row.isGroup) {
          // Show Review Schedule for balancing/overdue rows with a reviewable run — same
          // destination as the Period cell link, giving two entry points to the review session.
          const to = periodLinkTo(row.status, {
            serviceLineId: row.serviceLineId,
            runId: row.runId,
            organizationUnitId: row.organizationUnitId,
          });
          return to ? (
            <Button kind="secondary" size="sm" onClick={() => navigate(to)}>
              Review Schedule
            </Button>
          ) : null;
        }

        // Flag on: always surface the CTA as a period picker so QA can open Team Review
        // for any current/future period (see _team-review-period-menu). The button's
        // production gating (run_pending → earliest awaiting period) is bypassed.
        // TODO(WORK-6503): remove this debug branch (and TeamReviewPeriodMenu) when the
        // debug Review Team behavior is torn down.
        // https://app.asana.com/1/303134204339906/project/1213809590907449/task/1216012235109932
        if (isTeamReviewPeriodPickerEnabled) {
          return (
            <TeamReviewPeriodMenu
              serviceLineId={row.serviceLineId}
              periods={futurePeriods}
            />
          );
        }

        // Flag off (production): the single CTA, shown only for a service line awaiting
        // its balancer run, opening the earliest such period.
        return row.needsTeamReview && row.teamReviewPeriodId ? (
          <Button
            kind="primary"
            size="sm"
            onClick={(e) => {
              // The group row's onClick toggles expand; keep the CTA from also toggling.
              e.stopPropagation();
              navigate(
                `/works/schedule/dashboard/${row.serviceLineId}/team-review/${row.teamReviewPeriodId}`
              );
            }}
          >
            Review Team
          </Button>
        ) : null;
      },
    },
  ];

  const limit = paginationVariables.limit ?? DEFAULT_PAGE_SIZE;
  const offset = paginationVariables.offset ?? 0;

  return (
    <div className="schedule-periods-table">
      <FilteredTable
        rowKey="id"
        columns={columns}
        filters={filters}
        dataSource={rows}
        loading={loading}
        localStorageFilterKey={localStorageFilterKey}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        scroll={{ x: '100%' }}
        expandable={{
          expandRowByClick: false,
          expandedRowKeys,
          indentSize: 0,
          defaultExpandAllRows: true,
          onExpand: (_expanded: boolean, record: DashboardRowType) =>
            toggleExpand(record.id),
        }}
        rowClassName={(record: DashboardRowType) =>
          record.isGroup ? 'schedule-periods-table__group-row' : undefined
        }
        onRow={(record: DashboardRowType) => ({
          onClick: record.isGroup ? () => toggleExpand(record.id) : undefined,
        })}
        pagination={{
          total: result?.totalGroupCount ?? 0,
          current: Math.floor(offset / limit) + 1,
          pageSize: limit,
        }}
        locale={{
          emptyText: (
            <EmptyContent
              header="No scheduling periods"
              text="There are no scheduling periods to review yet."
            />
          ),
        }}
      />
      <style jsx>{`
        .schedule-periods-table
          :global(.schedule-periods-table__group-row > td) {
          font-weight: ${FontWeight.bold};
        }
        .schedule-periods-table :global(.schedule-periods-table__group-name) {
          font-weight: ${FontWeight.bold};
        }
      `}</style>
    </div>
  );
}

function formatPeriod(row: UnitRowType): string {
  return `${datetime(row.periodStart).format('MMM D')} – ${datetime(
    row.periodEnd
  ).format('MMM D, YYYY')}`;
}

function isPastDue(row: UnitRowType): boolean {
  return (
    row.status !== 'completed' &&
    datetime(row.dueDate).isBefore(datetime(), 'day')
  );
}
